import React from 'react';
import { Box, ButtonV2, CloseButton, DialogV2, DrawerV2, Flex, useToast } from '@withjoy/joykit';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useGuestIdentificationDialogContext } from './GuestIdentificationDialog.context';
import { EmailStep } from './EmailStep';
import { OtpStep } from './OtpStep';
import { useTranslation } from '@shared/core';
import { ReactComponent as ChevronBackIcon } from '@shared/joykit/packages/core/icons/svgs/16/chevron-back.svg';
import type { GuestIdentificationStep } from './GuestIdentificationDialog.types';

type GuestIdentificationDialogProps = Readonly<{
  eventId: string;
  onDismiss: () => void;
}>;

const DIALOG_OVERRIDES = {
  Root: {
    props: {
      backgroundColor: 'rgb(0, 0, 0, 0.4)',
      overflow: 'auto',
    },
  },
  Content: {
    props: {
      borderRadius: [0, '32px'],
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: ['0 0 120px', '0'],
      margin: ['inherit', 'auto'],
      overflow: 'hidden',
      backgroundColor: 'white',
    },
  },
} as const;

export const GuestIdentificationDialog: React.FC<GuestIdentificationDialogProps> = ({ eventId, onDismiss }) => {
  const { isOpen, onClose, step, setStep, email, setEmail } = useGuestIdentificationDialogContext();
  const isMobile = useIsMobileScreen();
  const { toast } = useToast();
  const { tq } = useTranslation('guestUnlock');

  const handleSkip = () => {
    onDismiss();
    onClose();
  };

  const handleCodeSent = (submittedEmail: string) => {
    setEmail(submittedEmail);
    setStep('otp');
  };

  const handleSuccess = (firstName: string | null | undefined) => {
    onDismiss();
    onClose();
    if (firstName) {
      toast(tq`Welcome, ${firstName}!`, { position: 'bottom-center' });
    } else {
      toast(tq`You're all set!`, { position: 'bottom-center' });
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
  };

  const showBackButton = step === 'otp';

  const renderStep = (currentStep: GuestIdentificationStep) => {
    switch (currentStep) {
      case 'otp':
        return (
          <OtpStep
            eventId={eventId}
            email={email}
            onSuccess={handleSuccess}
            onSkip={handleSkip}
          />
        );
      case 'email':
      default:
        return (
          <EmailStep
            eventId={eventId}
            onCodeSent={handleCodeSent}
            onSkip={handleSkip}
          />
        );
    }
  };

  if (isMobile) {
    return (
      <DrawerV2
        isOpen={isOpen}
        onClose={handleSkip}
        anchor="bottom"
        size="auto"
        useBackdrop
        overrides={{
          Content: {
            props: {
              borderTopLeftRadius: '1rem',
              borderTopRightRadius: '1rem',
              height: 'fit-content',
            },
          },
          Body: { props: { padding: '0' } },
          Header: { props: { padding: '0' } },
        }}
      >
        <DrawerV2.Header>
          <Flex
            width="100%"
            alignItems="center"
          >
            {showBackButton && (
              <ButtonV2
                startIcon={ChevronBackIcon}
                variant="ghost"
                shape="rounded"
                onClick={handleBackToEmail}
              >
                {tq`Back`}
              </ButtonV2>
            )}
            <CloseButton
              marginLeft="auto"
              onClick={handleSkip}
            />
          </Flex>
        </DrawerV2.Header>
        <DrawerV2.Body>{renderStep(step)}</DrawerV2.Body>
      </DrawerV2>
    );
  }

  return (
    <DialogV2
      size="lg"
      isOpen={isOpen}
      onClose={handleSkip}
      overrides={DIALOG_OVERRIDES}
    >
      <Box
        display="flex"
        padding="16px"
        height="56px"
        width="100%"
        alignItems="center"
      >
        {showBackButton && (
          <ButtonV2
            startIcon={ChevronBackIcon}
            variant="ghost"
            shape="rounded"
            onClick={handleBackToEmail}
          >
            {tq`Back`}
          </ButtonV2>
        )}
        <CloseButton
          marginLeft="auto"
          onClick={handleSkip}
        />
      </Box>
      <Box
        display="flex"
        height="100%"
      >
        {renderStep(step)}
      </Box>
    </DialogV2>
  );
};
